import { useEffect, useRef, useState } from 'react';
import CustomHistory from '../components/customHistory/CustomHistorySection';
import Footer from '../components/Footer/FooterSection';
import HeaderTwo from '../components/HeaderTwo/HeaderTwoSection';
import OrderSummary from '../components/OrderSummary/OrderSummary';
import { billingData } from '../interface/interfaces';
import '../styles/checkout.scss';
import { getBilling, getDeliveryFee } from '../api/requests';
import SkeletonLoader from '../components/SkeletonLoaders/DefaultLoader';
import { CheckIcon } from 'lucide-react';
import { useNotification } from '../context/NotificationContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { useCart } from '../context/CartContext';
import 'animate.css';

const Checkout = () => {
 const sectionRef = useRef<HTMLDivElement>(null);
 const { notify } = useNotification();
 const { trackBeginCheckout } = useAnalytics();
 const { cartData } = useCart();

 type pageDataProps = {
  billingData: billingData[];
  isLoading: boolean;
  couponId: string | null;
  deliveryFee: number;
  selectedBillingInfo: billingData | null;
 };

 const [pageData, setPageData] = useState<pageDataProps>({
  billingData: [],
  isLoading: false,
  couponId: null,
  deliveryFee: 0,
  selectedBillingInfo: null,
 });

 const [summaryLoader, setSummaryLoader] = useState(false);

 const setPageLoader = (value: boolean) => {
  setPageData((prev) => {
   return {
    ...prev,
    isLoading: value,
   };
  });
 };

 useEffect(() => {
  const func = async () => {
   try {
    setPageLoader(true);

    let bdat = await getBilling();

    setPageData((prev) => {
     return {
      ...prev,
      billingData: bdat,
      selectedBillingInfo: bdat[0],
     };
    });

    initSelect(bdat[0].id);

    // Track begin checkout event
    if (cartData.length > 0) {
      const totalValue = cartData.reduce((sum, item) => sum + item.totalAmount, 0);
      await trackBeginCheckout(totalValue, cartData);
    }
   } catch (error) {
    console.error(error);
   } finally {
    setPageLoader(false);
   }
  };

  func();
  // eslint-disable-next-line
 }, []);

 const scrollToOrder = () => {
  if (sectionRef.current) {
   const offset = 60;
   const elementPos =
    sectionRef.current.getBoundingClientRect().top +
    window.pageYOffset;
   window.scrollTo({
    top: elementPos - offset,
    behavior: 'smooth',
   });
  }
 };

 async function initSelect(id: string) {
  scrollToOrder();

  try {
   setSummaryLoader(true);

   let data = await getDeliveryFee(id);

   setPageData((prev) => {
    return {
     ...prev,
     deliveryFee: data.delivery_price,
    };
   });
  } catch (error: any) {
   notify.error(error.message);
  } finally {
   setSummaryLoader(false);
  }
 }

 async function handleAddressSelect(id: string) {
  let obj = pageData.billingData.find((data) => {
   return data.id === id;
  });

  if (obj) {
   setPageData((prev: any) => {
    return {
     ...prev,
     selectedBillingInfo: obj,
    };
   });

   scrollToOrder();

   try {
    setSummaryLoader(true);

    let data = await getDeliveryFee(id);

    setPageData((prev) => {
     return {
      ...prev,
      deliveryFee: data.delivery_price,
     };
    });
   } catch (error: any) {
    notify.error(error.message);
   } finally {
    setSummaryLoader(false);
   }
  }
 }

 const isInfoSelected = (id: string) => {
  if (pageData.selectedBillingInfo?.id === id) {
   return true;
  }

  return false;
 };

 return (
  <>
   <HeaderTwo></HeaderTwo>
   <CustomHistory></CustomHistory>

   <div className="checkout content">
    <div className="cartGrid">
     <div>
      {!pageData.isLoading && (
       <div>
        <div className="f1">
         <div className="title">Billing Information</div>

         <div className="text">
          Select your preferred Billing Address
         </div>
        </div>

        {pageData.billingData.length >= 1 && (
         <div className="addressGrid">
          {pageData.billingData.map((data, idx) => (
           <div
            className={`eachAddress animate__animated animate__fadeIn animate__fast ${
             isInfoSelected(data.id) ? 'active' : ''
            }`}
            key={idx}
            onClick={() => handleAddressSelect(data.id)}
           >
            <div className="indic">
             <div
              className="circ"
              style={{
               backgroundColor: `${
                isInfoSelected(data.id)
                 ? 'var(--primary)'
                 : 'transparent'
               }`,
              }}
             >
              {isInfoSelected(data.id) && (
               <CheckIcon
                size={13}
                strokeWidth={4}
                color="white"
               ></CheckIcon>
              )}
             </div>
            </div>

            <div className="data">
             <div className="icon profile"></div>
             <div className="text">{data.name}</div>
            </div>

            <div className="data">
             <div className="icon phone"></div>
             <div className="text">{data.billing_phone}</div>
            </div>

            <div className="data">
             <div className="icon addy"></div>
             <div className="text">{data.billing_address}</div>
            </div>
           </div>
          ))}
         </div>
        )}
       </div>
      )}

      {pageData.isLoading && <SkeletonLoader></SkeletonLoader>}
     </div>

     <div ref={sectionRef}>
      <OrderSummary
       deliveryFee={pageData.deliveryFee}
       billingData={pageData.selectedBillingInfo}
       loader={summaryLoader}
      ></OrderSummary>
     </div>
    </div>
   </div>

   <Footer></Footer>
  </>
 );
};

export default Checkout;
